/**
 * PublicLayout - Layout for public (unauthenticated) pages
 *
 * Version History:
 * v4.36 - 2026-10-29 - EXPO-QUALIFIER-01: optional hideAuth prop (default false). When true the header,
 *         the mobile header and the mobile drawer show no Sign In and no early-access button (used by
 *         /cleanup and /data-safety, Branko 2026-10-29). Every other page is unchanged.
 * v4.34 - 2026-10-28 - WEBSITE-DISABLE-01: temporarily disabled by Branko -
 *         row 783: the "Have an invite code?" link is removed from the header and the mobile drawer
 *         (the Request Early Access CTA is unchanged);
 *         row 781: the dark-mode switches in the mobile drawer and the footer bottom bar are removed
 *         (the portal always renders the light theme). The footer logo stays white on navy.
 * v4.10 - Header: strip to Pricing + Sign In (secondary) + CTA only
 *         Footer: 4-column redesign (brand / legal / contact / bottom bar)
 *         LinkedIn: linkedin.com/company/mylojic
 *         Copyright: © Data Edge Services LLC, doing business as MyLojic
 *         2026-03-23
 * v4.09 - Mobile responsive nav: hamburger + drawer, hide links on xs/sm, footer column on mobile - 2026-03-20
 * v4.08 - Fix footer logo: hardcode isDark=true (secondary.main is always dark navy) - 2026-03-20
 * v4.03 - Nav logo: logo-mark.svg (transparent bg, no tagline) + MyLojic wordmark text - 2026-03-20
 * v3.95 - Fix: Restore Features, About, Legal dropdown to nav (always visible); fix CTA /waitlist→/register - 2026-03-20
 * v3.94 - N-01/N-02/X-02: Mode-aware nav + scroll behavior + footer restructure - 2026-03-20
 * v3.88 - 2026-03-17 - GDPR-03: Add Cookie Policy to footer
 * v3.87 - 2026-03-17 - GDPR-03: Updated footer + Legal dropdown to use /legal/* URLs
 * v3.82 - 2026-03-15 - GDPR-01: Added Terms of Service to footer and Legal nav
 * v2.55 - 2026-02-04 - Migrated to Clerk authentication
 * v2.49 - 2026-02-03 - Added Legal menu (EULA, Privacy), updated Get Started to go to consent page
 */
import React, { useState, useEffect } from 'react'; // v4.34 - WEBSITE-DISABLE-01 (row 781): useContext dropped with the theme switch
import MyLojicLogo from '../MyLojicLogo';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Container,
  Link,
  Button,
  Skeleton,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import { useFeatureFlags, getPrimaryCTA } from '../../contexts/FeatureFlagContext';
import { analytics } from '../../utils/analytics';

const LINKEDIN_URL = 'https://www.linkedin.com/company/mylojic';
const CONTACT_EMAIL = 'hello@mylojic.com';
const COPYRIGHT = `© ${new Date().getFullYear()} Data Edge Services LLC, doing business as MyLojic`;

const PublicLayout: React.FC<{ hideAuth?: boolean }> = ({ hideAuth = false }) => {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const { siteMode, features, flagsLoaded } = useFeatureFlags();

  // v4.09 - Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);

  // N-02: Scroll-aware nav — transparent at top, solid on scroll
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const cta = getPrimaryCTA(siteMode);

  const handleLogin = () => navigate('/login');
  const handleCTAClick = () => {
    analytics.ctaClick('nav', siteMode);
    navigate(cta.href);
  };
  const closeMobileMenu = () => setMobileOpen(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <AppBar
        position="sticky"
        elevation={scrolled ? 2 : 0}
        sx={{
          bgcolor: scrolled ? 'background.paper' : 'transparent',
          backdropFilter: scrolled ? 'blur(8px)' : 'none',
          borderBottom: scrolled ? 1 : 0,
          borderColor: 'divider',
          transition: 'background-color 0.2s ease, backdrop-filter 0.2s ease, box-shadow 0.2s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: { xs: 60, md: 72 } }}>

            {/* Logo */}
            <Box
              component="div"
              sx={{ cursor: 'pointer', mr: { xs: 'auto', md: 4 }, display: 'flex', alignItems: 'center' }}
              onClick={() => navigate('/')}
            >
              <MyLojicLogo isDark={isDark} height={36} />
            </Box>

            {/* Desktop Nav — Pricing only, grows to push auth right */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3, flexGrow: 1, alignItems: 'center' }}>
              {flagsLoaded && features.pricing && (
                <Link
                  href="/#pricing"
                  underline="none"
                  sx={{ color: 'text.primary', fontWeight: 500, '&:hover': { color: 'primary.main' } }}
                >
                  Pricing
                </Link>
              )}

              {/* v4.34 - WEBSITE-DISABLE-01 (row 783) - "Have an invite code?" link temporarily disabled by Branko 2026-10-28. */}
            </Box>

            {/* Desktop Auth — Sign In (secondary text) + Get Started (CTA) */}
            {!hideAuth && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center' }}>
              <Link
                component="button"
                onClick={handleLogin}
                underline="none"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                Sign In
              </Link>

              {!flagsLoaded ? (
                <Skeleton variant="rounded" width={140} height={38} />
              ) : (
                <Button
                  variant="contained"
                  onClick={handleCTAClick}
                  sx={{
                    bgcolor: 'accent.main',
                    color: 'accent.contrastText',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': { bgcolor: 'accent.dark' },
                  }}
                >
                  {cta.text}
                </Button>
              )}
            </Box>
            )}

            {/* Mobile: compact CTA + hamburger */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
              {flagsLoaded && !hideAuth && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCTAClick}
                  sx={{
                    bgcolor: 'accent.main',
                    color: 'accent.contrastText',
                    fontWeight: 600,
                    px: 2,
                    fontSize: '0.8rem',
                    '&:hover': { bgcolor: 'accent.dark' },
                  }}
                >
                  {cta.text}
                </Button>
              )}
              <IconButton
                onClick={() => setMobileOpen(true)}
                size="small"
                sx={{ color: 'text.primary' }}
                aria-label="Open menu"
              >
                <MenuIcon />
              </IconButton>
            </Box>

          </Toolbar>
        </Container>
      </AppBar>

      {/* ── Mobile Drawer ── */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={closeMobileMenu}
        sx={{ display: { md: 'none' } }}
        PaperProps={{ sx: { width: 280, bgcolor: 'background.paper' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <MyLojicLogo isDark={isDark} height={28} />
          <IconButton onClick={closeMobileMenu} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <List disablePadding>
          {flagsLoaded && features.pricing && (
            <ListItemButton onClick={() => { navigate('/#pricing'); closeMobileMenu(); }}>
              <ListItemText primary="Pricing" />
            </ListItemButton>
          )}
          {/* v4.34 - WEBSITE-DISABLE-01 (row 783) - "Have an invite code?" drawer item temporarily disabled by Branko 2026-10-28. */}
          <Divider />
          <ListItemButton onClick={() => { navigate('/legal/privacy'); closeMobileMenu(); }}>
            <ListItemText primary="Privacy Policy" primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
          </ListItemButton>
          <ListItemButton onClick={() => { navigate('/legal/terms'); closeMobileMenu(); }}>
            <ListItemText primary="Terms of Service" primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
          </ListItemButton>
          <ListItemButton onClick={() => { navigate('/legal/cookies'); closeMobileMenu(); }}>
            <ListItemText primary="Cookie Policy" primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
          </ListItemButton>
          <ListItemButton onClick={() => { navigate('/legal/eula'); closeMobileMenu(); }}>
            <ListItemText primary="EULA" primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
          </ListItemButton>
        </List>

        {!hideAuth && (
        <Box sx={{ mt: 'auto', px: 2, py: 3, borderTop: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => { handleLogin(); closeMobileMenu(); }}
            sx={{ mb: 1.5 }}
          >
            Sign In
          </Button>
          {flagsLoaded && (
            <Button
              fullWidth
              variant="contained"
              onClick={() => { handleCTAClick(); closeMobileMenu(); }}
              sx={{ bgcolor: 'accent.main', color: 'accent.contrastText', '&:hover': { bgcolor: 'accent.dark' } }}
            >
              {cta.text}
            </Button>
          )}
          {/* v4.34 - WEBSITE-DISABLE-01 (row 781) - drawer dark-mode switch removed; the portal is light-only. */}
        </Box>
        )}
      </Drawer>

      {/* ── Main Content ── */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>

      {/* ── Footer ── v4.10: 4-column redesign */}
      <Box
        component="footer"
        sx={{
          bgcolor: 'secondary.main',
          color: 'text.onDark',
          pt: { xs: 5, md: 7 },
          pb: { xs: 3, md: 4 },
          px: 2,
        }}
      >
        <Container maxWidth="lg">

          {/* Columns */}
          <Grid container spacing={{ xs: 4, md: 6 }} sx={{ mb: { xs: 4, md: 6 } }}>

            {/* Column 1 — Brand */}
            <Grid item xs={12} sm={6} md={4}>
              {/* Footer bg is always secondary.main (dark navy) — logo must always be white text */}
              <MyLojicLogo isDark={true} height={32} />
              <Typography
                variant="body2"
                sx={{ mt: 1.5, color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', lineHeight: 1.5 }}
              >
                Ask your data, not consultants.
              </Typography>
            </Grid>

            {/* Column 2 — Legal */}
            <Grid item xs={6} sm={3} md={3}>
              <Typography
                variant="overline"
                sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1.5, fontSize: '0.7rem', display: 'block', mb: 1.5 }}
              >
                Legal
              </Typography>
              {[
                { label: 'Privacy Policy', href: '/legal/privacy' },
                { label: 'Terms of Service', href: '/legal/terms' },
                { label: 'Cookie Policy', href: '/legal/cookies' },
                { label: 'EULA', href: '/legal/eula' },
              ].map(({ label, href }) => (
                <Link
                  key={label}
                  href={href}
                  underline="none"
                  display="block"
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '0.875rem',
                    mb: 1,
                    '&:hover': { color: '#fff' },
                    transition: 'color 0.15s',
                  }}
                >
                  {label}
                </Link>
              ))}
            </Grid>

            {/* Column 3 — Contact */}
            <Grid item xs={6} sm={3} md={3}>
              <Typography
                variant="overline"
                sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1.5, fontSize: '0.7rem', display: 'block', mb: 1.5 }}
              >
                Contact
              </Typography>
              <Link
                href={`mailto:${CONTACT_EMAIL}`}
                underline="none"
                display="flex"
                alignItems="center"
                gap={0.75}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.875rem',
                  mb: 1.5,
                  '&:hover': { color: '#fff' },
                  transition: 'color 0.15s',
                }}
              >
                <EmailIcon sx={{ fontSize: '1rem' }} />
                {CONTACT_EMAIL}
              </Link>
              <Link
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                display="flex"
                alignItems="center"
                gap={0.75}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.875rem',
                  '&:hover': { color: '#fff' },
                  transition: 'color 0.15s',
                }}
              >
                <LinkedInIcon sx={{ fontSize: '1rem' }} />
                LinkedIn
              </Link>
            </Grid>

          </Grid>

          {/* Bottom bar — copyright (v4.34 - WEBSITE-DISABLE-01 row 781: dark-mode switch removed) */}
          <Box
            sx={{
              borderTop: '1px solid rgba(255,255,255,0.12)',
              pt: 3,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
              {COPYRIGHT}
            </Typography>
          </Box>

        </Container>
      </Box>

    </Box>
  );
};

export default PublicLayout;
